import * as Yup from 'yup';
import { Formik } from 'components/form/formik';
import toast from 'react-hot-toast';
import { useState } from 'react';

import { Button } from '../../../components/button/Button';
import { PageHeader } from '../../../components/PageHeader';
import { getDisplayError } from '../../../utils/get-display-error';
import { InputField } from '../../../components/input/InputField';
import { useTranslation } from '../../../contexts/translation-context';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../utils/track-event';
import { requestCarrierPortalAccount } from '../models';

const carrierPortalRequestAccountSchema = Yup.object().shape({
  name: Yup.string().required('errors.required'),
  email: Yup.string().email('errors.invalidEmail').required('errors.required'),
  companyName: Yup.string().required('errors.required'),
  companyNumber: Yup.string().required('errors.required'),
});

export const CarrierPortalRequestAccountPage = () => {
  const { i18n } = useTranslation();
  const [hasRequested, setHasRequested] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="h-screen">
      <PageHeader title={i18n('portal.requestAccount.requestCarrierAccount')} />

      <main className="p-8 min-h-screen w-full">
        <div className="flex flex-col justify-center h-full items-center max-w-md mx-auto">
          <h1 className="heading-one mb-8 mt-32">{i18n('portal.requestAccount.requestCarrierAccount')}</h1>

          <div className="w-full">
            <Formik
              initialValues={{
                name: '',
                email: '',
                companyName: '',
                companyNumber: '',
              }}
              validationSchema={carrierPortalRequestAccountSchema}
              onSubmit={async (values) => {
                try {
                  const { name, email, companyName, companyNumber } = values;

                  const result = await requestCarrierPortalAccount({
                    name,
                    email,
                    companyName,
                    companyNumber,
                  });

                  if (!result.success) {
                    throw new Error(i18n('portal.requestAccount.accountRequestFailed'));
                  }

                  trackEvent('carrier_portal_account_requested');
                  setHasRequested(true);

                  toast.success(i18n('portal.requestAccount.success'));
                } catch (err: any) {
                  captureException(err);
                  setError(getDisplayError(err));

                  toast.error(
                    i18n('portal.requestAccount.failed', {
                      error: getDisplayError(err),
                    }),
                  );
                }
              }}
            >
              {({ handleSubmit, isSubmitting }) => (
                <form onSubmit={handleSubmit}>
                  <InputField labelText={i18n('portal.requestAccount.name')} type="text" name="name" />
                  <InputField labelText={i18n('portal.requestAccount.email')} type="email" name="email" />
                  <InputField labelText={i18n('portal.requestAccount.companyName')} type="text" name="companyName" />
                  <InputField
                    labelText={i18n('portal.requestAccount.companyNumber')}
                    type="text"
                    name="companyNumber"
                  />

                  <div className="my-4">
                    {hasRequested && <p>{i18n('portal.requestAccount.success')}</p>}
                    {error && <p className="text-negative-text">{error}</p>}
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" color="secondary" isDisabled={isSubmitting} isLoading={isSubmitting}>
                      {i18n('portal.requestAccount.request')}
                    </Button>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>
      </main>
    </div>
  );
};
